// src/components/ContactUs.js
import React, { useState, useEffect } from "react";
import {
  Table,
  Input,
  Modal,
  Spin,
  Alert,
  Button,
  Select,
  Tooltip,
  Space,
  message as antMessage, // ✅ FIX: bring back message as antMessage
} from "antd";
import {
  DeleteOutlined,
  ExclamationCircleOutlined,
  SearchOutlined,
  FilterOutlined,
} from "@ant-design/icons";
import axiosInstance from "../utils/axiosInstance";

const { confirm } = Modal;
const { Option } = Select;

const ContactUs = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [sortOption, setSortOption] = useState("all");
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);

  // Fetch messages from backend
  useEffect(() => {
    const fetchMessages = async () => {
      try {
        const response = await axiosInstance.get("/admin/get-messages");
        setMessages(response.data.messages || []);
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    };
    fetchMessages();
  }, []);

  // DELETE handler with confirmation modal
  const showDeleteConfirm = (id) => {
    confirm({
      title: "Are you sure you want to delete this message?",
      icon: <ExclamationCircleOutlined />,
      content: "This action cannot be undone.",
      centered: true,
      okText: "Yes, Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          const response = await axiosInstance.delete(`/admin/contact-message/${id}`);
          if (response.data.success) {
            antMessage.success("Message deleted successfully");
            setMessages((prev) => prev.filter((msg) => msg.id !== id));
          } else {
            throw new Error("Delete failed");
          }
        } catch (err) {
          antMessage.error("Failed to delete message");
        }
      },
    });
  };

  // Filter messages for search using "name", "companyName", and "phoneNumber" keys.
  const filteredMessages = messages.filter(
    (msg) =>
      (msg.name || "").toLowerCase().includes(searchText.toLowerCase()) ||
      (msg.companyName || "").toLowerCase().includes(searchText.toLowerCase()) ||
      (msg.phoneNumber || "").toLowerCase().includes(searchText.toLowerCase())
  );

  // Sort the filtered messages based on sortOption
  const sortedMessages = [...filteredMessages].sort((a, b) => {
    if (sortOption === "nameAsc") {
      return (a.name || "").localeCompare(b.name || "");
    } else if (sortOption === "nameDesc") {
      return (b.name || "").localeCompare(a.name || "");
    } else if (sortOption === "dateAsc") {
      return new Date(a.createdAt) - new Date(b.createdAt);
    } else if (sortOption === "dateDesc") {
      return new Date(b.createdAt) - new Date(a.createdAt);
    }
    // "all" or default -> no change in order
    return 0;
  });

  // Show a centered loader while fetching data
  if (loading) {
    return (
      <div className="min-h-screen flex justify-center items-center">
        <Spin tip="Loading..." size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <Alert message="Error" description={error.message} type="error" showIcon />
      </div>
    );
  }

  // Function to show full message in modal
  const showFullMessage = (messageText) => {
    setSelectedMessage(messageText);
    setModalVisible(true);
  };

  // Table columns
  const columns = [
    {
      title: "Sr.No",
      key: "srNo",
      width: 70,
      render: (_, __, index) => (
        <div className="text-center font-medium text-gray-600">{index + 1}</div>
      ),
    },
    {
      title: "Full Name",
      dataIndex: "name",
      key: "name",
      width: 160,
      render: (name) => <div className="text-sm font-medium text-gray-900">{name || "-"}</div>,
    },
    {
      title: "Company Name",
      dataIndex: "companyName",
      key: "companyName",
      width: 180,
      render: (companyName) => <div className="text-sm text-gray-600">{companyName || "-"}</div>,
    },
    {
      title: "Phone Number",
      dataIndex: "phoneNumber",
      key: "phoneNumber",
      width: 160,
      render: (phone) => <div className="text-sm text-blue-600 font-medium">{phone || "-"}</div>,
    },
    {
      title: "Date",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 120,
      render: (text) => (
        <div className="text-sm text-gray-600 font-medium bg-gray-50 px-2 py-1 rounded text-center">
          {new Date(text).toLocaleDateString()}
        </div>
      ),
    },
    {
      title: "Message",
      dataIndex: "message",
      key: "message",
      width: 200,
      render: (text) => (
        <div className="text-sm text-gray-700 break-words">
          {text && text.length > 100 ? (
            <>
              {text.slice(0, 100)}...{" "}
              <Button
                type="link"
                onClick={() => showFullMessage(text)}
                className="p-0 h-auto text-blue-500 hover:text-blue-700"
              >
                View More
              </Button>
            </>
          ) : (
            text || "-"
          )}
        </div>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      align: "center",
      render: (_, record) => (
        <Tooltip title="Delete">
          <Button
            type="primary"
            danger
            icon={<DeleteOutlined />}
            onClick={() => showDeleteConfirm(record.id)}
            size="small"
          />
        </Tooltip>
      ),
    },
  ];

  return (
    <Spin spinning={loading} size="large" className="min-h-screen">
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 py-6 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Header Section */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2">Call Back Requests</h1>
                <p className="text-gray-600">Manage customer inquiries and callback requests</p>
              </div>
            </div>
          </div>

          {/* Filters Section */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Search Input */}
              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 flex items-center mb-2">
                  <SearchOutlined className="mr-2" />
                  Search Requests
                </label>
                <Input
                  placeholder="Search by name, company, phone..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  prefix={<SearchOutlined className="text-gray-400" />}
                  className="w-full h-10 rounded-lg"
                  allowClear
                />
              </div>

              {/* Sort Select */}
              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 flex items-center mb-2">
                  <FilterOutlined className="mr-2" />
                  Sort By
                </label>
                <Select
                  value={sortOption}
                  onChange={(value) => setSortOption(value)}
                  className="w-full h-10 rounded-lg"
                >
                  <Option value="all">All</Option>
                  <Option value="nameAsc">Name - A to Z</Option>
                  <Option value="nameDesc">Name - Z to A</Option>
                  <Option value="dateAsc">Date - Oldest First</Option>
                  <Option value="dateDesc">Date - Newest First</Option>
                </Select>
              </div>

              {/* Stats Card */}
              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 flex items-center mb-2">
                  <div className="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
                  Request Summary
                </label>
                <div className="bg-gray-50 rounded-lg border border-gray-200 p-3 h-10 flex items-center">
                  <div className="text-sm font-medium text-gray-900">
                    Total: {sortedMessages.length} requests
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Table Section */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
            <Table
              dataSource={sortedMessages}
              columns={columns}
              rowKey="id"
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                className: "px-6 py-4",
                pageSizeOptions: ["5", "10", "20", "50"],
              }}
              scroll={{ x: "max-content" }}
              className="modern-orders-table"
              rowClassName={() => "hover:bg-gray-50 transition-colors duration-200"}
              locale={{
                emptyText: (
                  <div className="text-center py-8">
                    <div className="text-lg font-medium text-gray-500 mb-2">
                      No callback requests found
                    </div>
                    <div className="text-sm text-gray-400">No messages have been received yet</div>
                  </div>
                ),
              }}
            />
          </div>
        </div>
      </div>

      {/* Modal to show full message */}
      <Modal
        title={
          <div className="flex items-center space-x-2">
            <ExclamationCircleOutlined className="text-blue-500" />
            <span>Full Message</span>
          </div>
        }
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setModalVisible(false)} className="rounded-lg">
            Close
          </Button>,
        ]}
        className="modern-modal"
      >
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
          <p className="text-gray-700 whitespace-pre-wrap">{selectedMessage}</p>
        </div>
      </Modal>

      <style jsx>{`
        .modern-orders-table .ant-table-thead > tr > th {
          background: #f8fafc;
          color: #374151;
          font-weight: 600;
          border-bottom: 2px solid #e5e7eb;
          white-space: nowrap;
          padding: 12px 16px;
        }

        .modern-orders-table .ant-table-tbody > tr > td {
          border-bottom: 1px solid #f1f5f9;
          padding: 12px 16px;
          transition: all 0.2s ease;
        }

        .modern-modal .ant-modal-content {
          border-radius: 16px;
          overflow: hidden;
        }

        .modern-modal .ant-modal-header {
          border-bottom: 1px solid #e5e7eb;
          background: #f8fafc;
        }
      `}</style>
    </Spin>
  );
};

export default ContactUs;
